import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { PercentageWheel } from "@/ds/custom/PercentageWheel";
import { Statistic, StatisticsBar } from "@/ds/custom/StatisticsBar";
import { QueryState } from "@/ds/custom/QueryState";
import { StatePanel } from "@/ds/custom/StatePanel";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import type { CollectionProgressResponseModel } from "@/features/collections/api/models/CollectionProgressResponseModel";
import type { GoalCollectionModel } from "@/features/collections/api/models/GoalCollectionModel";
import type { ProgressSummaryResponseModel } from "@/features/collections/api/models/ProgressSummaryResponseModel";
import type { TransactionSummaryModel } from "@/features/collections/api/models/TransactionSummaryModel";
import { ProgressSummarySortOrderOptions } from "@/features/collections/ProgressSummarySortOrder";
import { TransactionSummariesSortOrderOptions } from "@/features/collections/TransactionSummariesSortOrder";
import { useCollection } from "@/features/collections/api/useCollection";
import { useCollectionProgress } from "@/features/collections/api/useCollectionProgress";
import { useGoalCollection } from "@/features/collections/api/useGoalCollection";
import { useProgressSummaries } from "@/features/collections/api/useProgressSummaries";
import { useTransactionSummaries } from "@/features/collections/api/useTransactionSummaries";
import { CollectionMarketValueDisplay } from "@/features/collections/components/CollectionMarketValueDisplay";
import { ModalCreateTransaction, type ModalCreateTransactionVariantInformation } from "@/features/collections/components/ModalCreateTransaction";
import { ModalShareableLink } from "@/features/collections/components/ModalShareableLink";
import { ProgressBarWithButton } from "@/features/collections/components/ProgressBarWithButton";
import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { Padlock } from "@/features/common/components/Padlock";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import type { Money } from "@/lib/domain/Money";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { SearchBar } from "@/features/common/components/SearchBar";
import { Button } from "@/ds/shadcn/components/ui/button";
import { DownloadIcon, PackageOpenIcon, Share2Icon, SortAscIcon, TargetIcon } from "lucide-react";
import type { FC } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useState } from "react";

const LIST_VIEW_HEADER_LABELS = ["Product", "Value", "Quantity", "Total", "Invested", "Gain"];
const LIST_VIEW_HEADER_CLASS_NAMES = ["min-w-0", "w-24 @2xl:w-32", "w-24", "w-24 @2xl:w-32", "w-24 @2xl:w-32", "w-48 @2xl:w-48"];

const PROGRESS_LIST_VIEW_HEADER_LABELS = ["Product", "Progress", "Value", "Quantity", "Goal", "Total", "CTC", "Gain"];
const PROGRESS_LIST_VIEW_HEADER_CLASS_NAMES = ["min-w-0", "w-32 @2xl:w-40", "w-24 @2xl:w-32", "w-20", "w-20", "w-24 @2xl:w-32", "w-24 @2xl:w-32", "w-48 @2xl:w-48"];

export const TabCollectionSummary: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;

	const goalCollection = useGoalCollection(collectionId);

	return (
		<QueryState query={goalCollection}>
			{
				data => data.goalCollection
					? <TabCollectionSummaryProgress collectionId={collectionId} goalCollection={data.goalCollection} />
					: <TabCollectionSummaryTransactions collectionId={collectionId} />
			}
		</QueryState>
	);
};

interface TabCollectionSummaryInternalProps {
	collectionId: string;
}

interface TabCollectionSummaryProgressProps {
	collectionId: string;
	goalCollection: GoalCollectionModel;
}

const TabCollectionSummaryTransactions: FC<TabCollectionSummaryInternalProps> = props => {
	const { collectionId } = props;
	const { state, dispatchSetCollectionSummaryTransactionSortOrder } = useUserPreferencesState();
	const [searchQuery, setSearchQuery] = useState("");
	const collection = useCollection(collectionId);
	const transactionSummaries = useTransactionSummaries(collectionId, state.collectionSummaryTransactionSortOrder, searchQuery);

	return (
		<>
			<HeaderBarTools>
				<HeaderBarSelector
					value={state.collectionSummaryTransactionSortOrder}
					onChange={dispatchSetCollectionSummaryTransactionSortOrder}
					placeholder="Sort"
					icon={SortAscIcon}
					options={TransactionSummariesSortOrderOptions} />

				<CollectionExportButton collectionId={collectionId} />
				<CollectionShareButton collectionId={collectionId} />
			</HeaderBarTools>

			{
				collection.data &&
				<>
					<CollectionMarketValueDisplay collection={collection.data} />
					<CollectionStatisticsBar collection={collection.data} />
				</>
			}

			<ErrorDisplay error={transactionSummaries.error} />

			<SearchBar value={searchQuery} onChange={setSearchQuery} />

			<DataView
				forQuery={transactionSummaries}
				getKey={x => x.variantId}
				listViewHeaderLabels={LIST_VIEW_HEADER_LABELS}
				listViewHeaderClassNames={LIST_VIEW_HEADER_CLASS_NAMES}
				listComponent={TransactionSummaryListItem}
				galleryComponent={TransactionSummaryGalleryItem}
				additionalProps={{ collectionId }}
				galleryMinWidth={208}>

				<StatePanel
					icon={PackageOpenIcon}
					title="No Transactions Yet"
					description="Record a transaction for this collection to see it summarised here." />
			</DataView>
		</>
	);
};

interface CollectionExportButtonProps {
	collectionId: string;
}

const CollectionExportButton: FC<CollectionExportButtonProps> = props => {
	const { collectionId } = props;
	const navigate = useNavigate();

	return (
		<Button variant="outline" className="@md:w-24" onClick={() => navigate(`/collections/${collectionId}/csv-export`)}>
			<DownloadIcon />
			Export
		</Button>
	);
};

interface CollectionShareButtonProps {
	collectionId: string;
}

const CollectionShareButton: FC<CollectionShareButtonProps> = props => {
	const { collectionId } = props;
	const [open, setOpen] = useState(false);

	return (
		<>
			<Button variant="outline" className="@md:w-24" onClick={() => setOpen(true)}>
				<Share2Icon />
				Share
			</Button>

			<ModalShareableLink open={open} onOpenChange={setOpen} collectionId={collectionId} />
		</>
	);
};

interface CollectionStatisticsBarProps {
	collection: CollectionModel;
	progress?: CollectionProgressResponseModel;
}

const CollectionStatisticsBar: FC<CollectionStatisticsBarProps> = props => {
	const { collection, progress } = props;

	const progressPercentage = progress && progress.totalGoalQuantity > 0
		? Math.floor(100 * Math.min(progress.totalActualQuantity / progress.totalGoalQuantity, 1))
		: null;

	return (
		<StatisticsBar>
			{
				progress &&
				<Statistic title="Progress">
					<div className="flex items-center gap-1.5">
						<PercentageWheel current={progress.totalActualQuantity} max={progress.totalGoalQuantity} className="size-5 text-primary" />
						{progressPercentage ?? 0}%
					</div>
				</Statistic>
			}
			{
				progress &&
				<Statistic title="Cost to Complete">
					<MoneyDisplay mode="Standard" money={progress.costToComplete} />
				</Statistic>
			}
			{
				progress &&
				<Statistic title="Goal Quantity">{progress.totalGoalQuantity}</Statistic>
			}
			<Statistic title="Total Quantity">
				{collection.quantity}
			</Statistic>
			<Statistic title="Invested">
				<Padlock on={collection.invested}>
					{x => <MoneyDisplay mode="Standard" money={x} />}
				</Padlock>
			</Statistic>
			<Statistic title="Profit">
				<Padlock on={collection.profit}>
					{x => <MoneyDisplay mode="Delta" money={x} />}
				</Padlock>
			</Statistic>
			<Statistic title="Cash Position">
				<Padlock on={collection.cashPosition}>
					{x => <MoneyDisplay mode="Delta" money={x} />}
				</Padlock>
			</Statistic>
		</StatisticsBar>
	);
};

interface GoalCollectionBannerProps {
	goalCollection: GoalCollectionModel;
}

const GoalCollectionBanner: FC<GoalCollectionBannerProps> = props => {
	const { goalCollection } = props;

	return (
		<div className="border-b">
			<div className="flex items-center gap-2 px-4 py-3">
				<TargetIcon className="size-4 shrink-0 text-muted-foreground" />
				<span className="type-description whitespace-nowrap">Goal: </span>
				<Link to={`/collections/${goalCollection.goalCollectionId}`} className="type-item hover:underline truncate">
					{goalCollection.name}
				</Link>
			</div>
		</div>
	);
};

const TabCollectionSummaryProgress: FC<TabCollectionSummaryProgressProps> = props => {
	const { collectionId, goalCollection } = props;
	const { state, dispatchSetCollectionSummaryProgressSortOrder } = useUserPreferencesState();
	const [searchQuery, setSearchQuery] = useState("");
	const collection = useCollection(collectionId);
	const collectionProgress = useCollectionProgress(collectionId);
	const progressSummaries = useProgressSummaries(collectionId, searchQuery, true, state.collectionSummaryProgressSortOrder);
	const [transactionModalVariant, setTransactionModalVariant] = useState<ModalCreateTransactionVariantInformation | null>(null);

	return (
		<>
			<HeaderBarTools>
				<HeaderBarSelector
					value={state.collectionSummaryProgressSortOrder}
					onChange={dispatchSetCollectionSummaryProgressSortOrder}
					placeholder="Sort"
					icon={SortAscIcon}
					options={ProgressSummarySortOrderOptions} />

				<CollectionExportButton collectionId={collectionId} />
				<CollectionShareButton collectionId={collectionId} />
			</HeaderBarTools>

			{
				collection.data &&
				<>
					<CollectionMarketValueDisplay collection={collection.data} />
					<CollectionStatisticsBar collection={collection.data} progress={collectionProgress.data} />
				</>
			}

			<GoalCollectionBanner goalCollection={goalCollection} />

			<ErrorDisplay error={progressSummaries.error || collectionProgress.error} />

			<SearchBar value={searchQuery} onChange={setSearchQuery} />

			<DataView
				forQuery={progressSummaries}
				getKey={x => x.variantId}
				listViewHeaderLabels={PROGRESS_LIST_VIEW_HEADER_LABELS}
				listViewHeaderClassNames={PROGRESS_LIST_VIEW_HEADER_CLASS_NAMES}
				listComponent={ProgressSummaryListItem}
				galleryComponent={ProgressSummaryGalleryItem}
				additionalProps={{ collectionId, onRecordTransaction: setTransactionModalVariant }}
				galleryMinWidth={208}>

				<StatePanel
					icon={TargetIcon}
					title="No Progress Yet"
					description="Your goal collection has no items configured to track progress against." />
			</DataView>

			<ModalCreateTransaction
				collectionId={collectionId}
				variant={transactionModalVariant}
				onClose={() => setTransactionModalVariant(null)} />
		</>
	);
};

interface CollectionSummaryItemAdditionalProps {
	collectionId: string;
}

interface ProgressSummaryItemAdditionalProps extends CollectionSummaryItemAdditionalProps {
	onRecordTransaction: (variant: ModalCreateTransactionVariantInformation) => void;
}

const TransactionSummaryListItem: FC<TransactionSummaryModel & CollectionSummaryItemAdditionalProps> = props => {
	const {
		collectionId,
		variantId,
		productName,
		fcId,
		imageUrl,
		quantity,
		marketValue,
		invested,
		gain
	} = props;

	const navigate = useNavigate();

	const value: Money = quantity !== 0
		? { value: marketValue.value / quantity, currency: marketValue.currency }
		: marketValue;

	return (
		<ListItem onClick={() => navigate(`/collections/${collectionId}/transactions/${variantId}`)}>
			<TableCell>
				<SmallProductDisplay productName={productName} fcId={fcId} imageUrl={imageUrl} />
			</TableCell>
			<SummaryValueCells value={value} quantity={quantity} total={marketValue} invested={invested} gain={gain} />
		</ListItem>
	);
};

const TransactionSummaryGalleryItem: FC<TransactionSummaryModel & CollectionSummaryItemAdditionalProps> = props => {
	const {
		collectionId,
		variantId,
		productName,
		fcId,
		imageUrl,
		quantity,
		marketValue,
		invested,
		gain
	} = props;

	const navigate = useNavigate();

	const value: Money = quantity !== 0
		? { value: marketValue.value / quantity, currency: marketValue.currency }
		: marketValue;

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={imageUrl} onClick={() => navigate(`/collections/${collectionId}/transactions/${variantId}`)}>
			<SummaryValueProperties value={value} quantity={quantity} total={marketValue} invested={invested} gain={gain} />
		</GalleryItem>
	);
};

const ProgressSummaryListItem: FC<ProgressSummaryResponseModel & ProgressSummaryItemAdditionalProps> = props => {
	const {
		collectionId,
		onRecordTransaction,
		variantId,
		productName,
		fcId,
		imageUrl,
		actualQuantity,
		goalQuantity,
		marketValue,
		invested,
		gain
	} = props;

	const navigate = useNavigate();

	const total: Money = { value: marketValue.value * actualQuantity, currency: marketValue.currency };
	const ctc: Money = { value: marketValue.value * Math.max(goalQuantity - actualQuantity, 0), currency: marketValue.currency };

	return (
		<ListItem onClick={() => navigate(`/collections/${collectionId}/transactions/${variantId}`)}>
			<TableCell>
				<SmallProductDisplay productName={productName} fcId={fcId} imageUrl={imageUrl} />
			</TableCell>
			<TableCell>
				<ProgressBarWithButton current={actualQuantity} max={goalQuantity} onClick={() => onRecordTransaction(props)} />
			</TableCell>
			<ProgressSummaryValueCells value={marketValue} actualQuantity={actualQuantity} goalQuantity={goalQuantity} total={total} ctc={ctc} invested={invested} gain={gain} />
		</ListItem>
	);
};

const ProgressSummaryGalleryItem: FC<ProgressSummaryResponseModel & ProgressSummaryItemAdditionalProps> = props => {
	const {
		collectionId,
		onRecordTransaction,
		variantId,
		productName,
		fcId,
		imageUrl,
		actualQuantity,
		goalQuantity,
		marketValue,
		invested,
		gain
	} = props;

	const navigate = useNavigate();

	const total: Money = { value: marketValue.value * actualQuantity, currency: marketValue.currency };
	const ctc: Money = { value: marketValue.value * Math.max(goalQuantity - actualQuantity, 0), currency: marketValue.currency };

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={imageUrl} onClick={() => navigate(`/collections/${collectionId}/transactions/${variantId}`)}>
			<GalleryItemProperty>
				<ProgressBarWithButton className="w-full" current={actualQuantity} max={goalQuantity} onClick={() => onRecordTransaction(props)} />
			</GalleryItemProperty>
			<ProgressSummaryValueProperties value={marketValue} actualQuantity={actualQuantity} goalQuantity={goalQuantity} total={total} ctc={ctc} invested={invested} gain={gain} />
		</GalleryItem>
	);
};

interface SummaryValueProps {
	value: Money;
	quantity: number;
	total: Money;
	invested: Money | undefined;
	gain: Money | undefined;
}

const SummaryValueCells: FC<SummaryValueProps> = props => {
	const { value, quantity, total, invested, gain } = props;

	return (
		<>
			<TableCell><MoneyDisplay mode="Standard" money={value} /></TableCell>
			<TableCell>{quantity}</TableCell>
			<TableCell><MoneyDisplay mode="Standard" money={total} /></TableCell>
			<TableCell>
				<Padlock on={invested}>
					{x => <MoneyDisplay mode="Standard" money={x} />}
				</Padlock>
			</TableCell>
			<TableCell>
				<Padlock on={gain}>
					{x => <MoneyDisplay mode="DeltaPercentage" money={x} originalValue={invested?.value || 0} />}
				</Padlock>
			</TableCell>
		</>
	);
};

const SummaryValueProperties: FC<SummaryValueProps> = props => {
	const { value, quantity, total, invested, gain } = props;

	return (
		<>
			<GalleryItemProperty label="Value">
				<MoneyDisplay mode="Standard" money={value} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Quantity">
				{quantity}
			</GalleryItemProperty>
			<GalleryItemProperty label="Total">
				<MoneyDisplay mode="Standard" money={total} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Invested">
				<Padlock on={invested}>
					{x => <MoneyDisplay mode="Standard" money={x} />}
				</Padlock>
			</GalleryItemProperty>
			<GalleryItemProperty label="Gain">
				<Padlock on={gain}>
					{x => <MoneyDisplay mode="DeltaPercentage" money={x} originalValue={invested?.value || 0} />}
				</Padlock>
			</GalleryItemProperty>
		</>
	);
};

interface ProgressSummaryValueProps {
	value: Money;
	actualQuantity: number;
	goalQuantity: number;
	total: Money;
	ctc: Money;
	invested: Money | undefined;
	gain: Money | undefined;
}

const ProgressSummaryValueCells: FC<ProgressSummaryValueProps> = props => {
	const { value, actualQuantity, goalQuantity, total, ctc, invested, gain } = props;

	return (
		<>
			<TableCell><MoneyDisplay mode="Standard" money={value} /></TableCell>
			<TableCell>{actualQuantity}</TableCell>
			<TableCell>{goalQuantity}</TableCell>
			<TableCell><MoneyDisplay mode="Standard" money={total} /></TableCell>
			<TableCell><MoneyDisplay mode="Standard" money={ctc} /></TableCell>
			<TableCell>
				<Padlock on={gain}>
					{x => <MoneyDisplay mode="DeltaPercentage" money={x} originalValue={invested?.value || 0} />}
				</Padlock>
			</TableCell>
		</>
	);
};

const ProgressSummaryValueProperties: FC<ProgressSummaryValueProps> = props => {
	const { value, actualQuantity, goalQuantity, total, ctc, invested, gain } = props;

	return (
		<>
			<GalleryItemProperty label="Value">
				<MoneyDisplay mode="Standard" money={value} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Quantity">
				{actualQuantity}
			</GalleryItemProperty>
			<GalleryItemProperty label="Goal">
				{goalQuantity}
			</GalleryItemProperty>
			<GalleryItemProperty label="Total">
				<MoneyDisplay mode="Standard" money={total} />
			</GalleryItemProperty>
			<GalleryItemProperty label="CTC">
				<MoneyDisplay mode="Standard" money={ctc} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Gain">
				<Padlock on={gain}>
					{x => <MoneyDisplay mode="DeltaPercentage" money={x} originalValue={invested?.value || 0} />}
				</Padlock>
			</GalleryItemProperty>
		</>
	);
};
